import React, {
  ComponentType,
  FunctionComponent,
  useMemo,
  useCallback,
  useState,
  useEffect,
} from 'react';
import { useTranslate, useDataProvider } from 'react-admin';
import { useForm } from 'react-final-form';
import AsyncSelect from 'react-select/async';
import { components, MenuListProps } from 'react-select';

export type ManualEntityChoiceConfig = {
  // Sentinel id stored in the field's value when the "manual" option is picked
  manualId: string;
  // Resource searched by the autocomplete (e.g. 'companies', 'users')
  resource: string;
  // Extra filter merged with the free-text search (e.g. { user_kind: 'client' })
  searchFilter: Record<string, any>;
  // Boolean field toggled to reveal the manual-entry nested fields
  activeFieldName: string;
  manualChoiceLabelKey: string;
  fieldLabelKey: string;
  getOptionLabel: (item: any) => string;
  // Extra side effects when a real entity is picked (e.g. pre-filling billing fields)
  onSelectReal?: (
    value: string | number,
    form: any,
    dataProvider: any,
    t: (key: string, options?: any) => string
  ) => void | Promise<void>;
  // Extra side effects when the manual option is picked (e.g. resetting nested fields)
  onSelectManual?: (
    form: any,
    t: (key: string, options?: any) => string
  ) => void;
};

// Shared by withManualClientChoice and withManualExpertChoice: an autocomplete
// searching a resource, with a "create manually" option appended to the menu
// that reveals nested fields instead of picking an existing record.
const createManualEntityChoice = (
  config: ManualEntityChoiceConfig
): ((Component: ComponentType<any>) => FunctionComponent) => {
  const {
    manualId,
    resource,
    searchFilter,
    activeFieldName,
    manualChoiceLabelKey,
    fieldLabelKey,
    getOptionLabel,
    onSelectReal,
    onSelectManual,
  } = config;

  return (Component: ComponentType<any>): FunctionComponent => {
    const Extension: FunctionComponent<any> = ({
      onChange,
      value,
      ...props
    }) => {
      const t = useTranslate();
      const form = useForm();
      const dataProvider = useDataProvider();

      const manualChoiceLabel = useMemo(
        () => `--- ${t(manualChoiceLabelKey)} ---`,
        [t]
      );

      const effectiveOnChange = onChange || props.input?.onChange;
      const effectiveValue = value !== undefined ? value : props.input?.value;

      const [currentOption, setCurrentOption] = useState<{
        value: string | number;
        label: string | number;
      } | null>(null);

      useEffect(() => {
        if (currentOption?.value === effectiveValue) return;

        if (effectiveValue && effectiveValue !== manualId) {
          dataProvider
            .getOne(resource, { id: effectiveValue })
            .then(({ data }) => {
              if (data) {
                setCurrentOption({
                  value: data.id,
                  label: getOptionLabel(data) || data.id,
                });
              }
            })
            .catch((e) =>
              console.error(`Error fetching initial ${resource}:`, e)
            );
        } else if (effectiveValue === manualId) {
          setCurrentOption({ value: manualId, label: manualChoiceLabel });
        } else if (!effectiveValue) {
          setCurrentOption(null);
        }
      }, [effectiveValue, dataProvider, manualChoiceLabel]);

      const handleSelect = useCallback(
        async (option: any) => {
          const val = option ? option.value : null;
          setCurrentOption(option);

          const isManual = val === manualId;

          if (effectiveOnChange) {
            effectiveOnChange(val);
          }
          form.change(activeFieldName, isManual);

          if (!isManual && val) {
            await onSelectReal?.(val, form, dataProvider, t);
          } else if (isManual) {
            onSelectManual?.(form, t);
          }
        },
        [effectiveOnChange, form, dataProvider, t]
      );

      const CustomMenuList = useCallback(
        (menuListProps: MenuListProps<any, any, any>) => (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '40vh',
            }}
          >
            <components.MenuList {...menuListProps}>
              {menuListProps.children}
            </components.MenuList>

            <div
              style={{
                padding: '12px',
                background: '#fcfcfc',
                borderTop: '1px solid #eee',
                textAlign: 'center',
                cursor: 'pointer',
                color: '#666',
                fontSize: '0.9rem',
                fontWeight: 500,
              }}
              onClick={() => {
                handleSelect({ value: manualId, label: manualChoiceLabel });
                if (menuListProps.selectProps.menuIsOpen) {
                  menuListProps.selectProps.onMenuClose?.();
                }
              }}
              onMouseEnter={(e) => {
                (e.target as HTMLDivElement).style.backgroundColor = '#f5f5f5';
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLDivElement).style.backgroundColor = '#fcfcfc';
              }}
            >
              {manualChoiceLabel}
            </div>
          </div>
        ),
        [manualChoiceLabel, handleSelect]
      );

      const loadOptions = async (inputValue: string) => {
        try {
          const { data } = await dataProvider.getList(resource, {
            pagination: { page: 1, perPage: 50 },
            sort: { field: 'id', order: 'DESC' },
            filter: { q: inputValue, ...searchFilter },
          });

          const results = data.map((item: any) => ({
            value: item.id,
            label: getOptionLabel(item) || item.id,
          }));

          if (inputValue) {
            const lowerInput = inputValue.toLowerCase();

            return results.filter((option) =>
              option.label.toString().toLowerCase().includes(lowerInput)
            );
          }

          return results;
        } catch (error) {
          console.error(`Failed to load ${resource} list:`, error);

          return [];
        }
      };

      return (
        <div style={{ marginBottom: 24 }}>
          <label
            style={{
              color: 'rgba(0, 0, 0, 0.54)',
              fontSize: '0.75rem',
              fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
              marginBottom: 4,
              display: 'block',
            }}
          >
            {t(fieldLabelKey)}
          </label>
          <AsyncSelect
            cacheOptions
            loadOptions={loadOptions}
            defaultOptions
            isClearable
            components={{ MenuList: CustomMenuList }}
            placeholder={t('ra.action.search', { _: 'Rechercher...' })}
            value={currentOption}
            onChange={handleSelect}
            styles={{
              control: (base: any) => ({
                ...base,
                border: 0,
                boxShadow: 'none',
                borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
                borderRadius: 0,
                backgroundColor: 'transparent',
                minHeight: '32px',
                '&:hover': {
                  borderBottom: '2px solid rgba(0, 0, 0, 0.87)',
                },
              }),
              indicatorSeparator: () => ({ display: 'none' }),
              dropdownIndicator: (base: any) => ({
                ...base,
                color: 'rgba(0, 0, 0, 0.54)',
              }),
              menu: (base: any) => ({
                ...base,
                zIndex: 9999,
                boxShadow: '0px 2px 10px rgba(0,0,0,0.1)',
              }),
              option: (base: any, state: any) => ({
                ...base,
                backgroundColor: state.isFocused ? '#f5f5f5' : 'white',
                color: 'black',
                cursor: 'pointer',
                padding: '10px 12px',
              }),
            }}
          />
        </div>
      );
    };

    Extension.displayName = `manualEntityChoice(${resource})(${
      Component.displayName || Component.name || 'Component'
    })`;

    return Extension;
  };
};

export default createManualEntityChoice;
